import React, { useState, useEffect, useMemo } from "react";
import CopyIdButton from "../components/CopyIdButton";
import { useTableColumnFilters, FilterTh } from "../components/tableFilters";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { Order, OrderStatus, OrderUnit } from "../types";
import { orderService } from "../services/dataService";
import { useAuth } from "../context/AuthContext";
import ImageManager from "../components/ImageManager";
import { turkishIncludes } from "../utils/formatters";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import "./PurchaseOrders.css";
import "./ModelistMyOrders.css";

const STATUS_CHIPS: [OrderStatus | "all", string][] = [
  ["all", "Tümü"],
  [OrderStatus.Atanmadi, "Atanmadı"],
  [OrderStatus.Islemde, "İşlemde"],
  [OrderStatus.Tamamlandi, "Tamamlandı"],
  [OrderStatus.IptalEdildi, "İptal"],
];
const STATUS_BADGE: Record<number, string> = {
  [OrderStatus.Atanmadi]: "muted",
  [OrderStatus.Islemde]: "info",
  [OrderStatus.Tamamlandi]: "ok",
  [OrderStatus.IptalEdildi]: "err",
};
const isOpenStatus = (s?: OrderStatus) => s !== OrderStatus.Tamamlandi && s !== OrderStatus.IptalEdildi;
const DAY = 86400000;
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); };
/** Açık işte termine kalan gün (negatif = geçti); kapalı işte ya da termin yoksa null. */
const daysLeft = (o: Order): number | null => {
  if (!o.deadline || !isOpenStatus(o.status)) return null;
  const d = new Date(o.deadline); d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - startOfToday()) / DAY);
};

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12a8 8 0 1 1-2.3-5.6" /><path d="M20 4v5h-5" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  filterOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h18l-7 8v6l-4-2v-4z" /><path d="M17 17l4 4M21 17l-4 4" /></svg>,
  qr: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>,
  image: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>,
  open: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>,
};

const ModelistMyOrders: React.FC = () => {
  // Kullanıcıya atanmış TÜM siparişler; durum süzgeci istemcide uygulanır (çip sayıları ve KPI'lar için)
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showImageManager, setShowImageManager] = useState(false);
  const [selectedOrderForImages, setSelectedOrderForImages] = useState<
    string | null
  >(null);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "all">("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [expandedTechnics, setExpandedTechnics] = useState<Set<string>>(
    new Set(),
  );

  // QR Print Modal state
  const [qrPrintModal, setQrPrintModal] = useState<{
    show: boolean;
    order: Order | null;
    qrCodeDataUrl: string | null;
  }>({ show: false, order: null, qrCodeDataUrl: null });

  const navigate = useNavigate();
  const { user } = useAuth();

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // QR Modal'ı aç
  const handleShowQrModal = async (order: Order) => {
    let qrDataUrl: string | null = null;
    try {
      if (order.qrCodeUrl || order.qrCode) {
        qrDataUrl = await QRCode.toDataURL(
          order.qrCodeUrl || order.qrCode || "",
          {
            width: 300,
            margin: 2,
          },
        );
      }
    } catch (error) {
      console.error("QR kod oluşturulamadı:", error);
    }
    setQrPrintModal({ show: true, order, qrCodeDataUrl: qrDataUrl });
  };
  const closeQrModal = () => setQrPrintModal({ show: false, order: null, qrCodeDataUrl: null });

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // QR modalı Esc ile kapanır
  useEffect(() => {
    if (!qrPrintModal.show) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); closeQrModal(); } };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [qrPrintModal.show]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await orderService.getAll();

      // Siparişleri oluşturulma tarihine göre tersten sırala (en yeni en üstte)
      const sortedData = [...data].sort((a, b) => {
        const dateA = new Date(a.createdAt || a.acceptanceDate || 0).getTime();
        const dateB = new Date(b.createdAt || b.acceptanceDate || 0).getTime();
        return dateB - dateA;
      });

      // Sadece bu kullanıcıya atanan siparişleri filtrele
      setOrders(sortedData.filter((order) => order.modelistUserId === user?.userId));
      setLoadError(false);
    } catch (error: any) {
      console.error("❌ Failed to load orders:", error);
      setLoadError(true);
      setAlertModal({
        isOpen: true,
        message: "Siparişler yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
      setFirstLoad(false);
    }
  };

  const getStatusText = (status?: OrderStatus): string => {
    if (status === undefined || status === null) return "Atanmadı";
    switch (status) {
      case OrderStatus.Atanmadi:
        return "Atanmadı";
      case OrderStatus.Islemde:
        return "İşlemde";
      case OrderStatus.Tamamlandi:
        return "Tamamlandı";
      case OrderStatus.IptalEdildi:
        return "İptal Edildi";
      default:
        return "Atanmadı";
    }
  };

  const getUnitText = (unit?: OrderUnit): string => {
    const unitMap: { [key: number]: string } = {
      [OrderUnit.Adet]: "Adet",
      [OrderUnit.Metre]: "Metre",
      [OrderUnit.Takim]: "Takım",
      [OrderUnit.Pano]: "Pano",
    };
    return unitMap[unit ?? OrderUnit.Adet] || "Adet";
  };

  // Durum + arama süzgeci
  const getFilteredOrders = (ordersToFilter: Order[]) => {
    const byStatus = statusFilter === "all" ? ordersToFilter : ordersToFilter.filter((order) => order.status === statusFilter);
    if (!searchTerm.trim()) return byStatus;

    return byStatus.filter((order) => {
      return (
        turkishIncludes(order.orderId || "", searchTerm) ||
        turkishIncludes(order.firm?.firmName || "", searchTerm) ||
        turkishIncludes(order.model?.modelName || "", searchTerm) ||
        turkishIncludes(order.model?.modelCode || "", searchTerm) ||
        turkishIncludes(order.workshop?.name || "", searchTerm) ||
        turkishIncludes(getStatusText(order.status), searchTerm) ||
        order.quantity?.toString().includes(searchTerm.trim())
      );
    });
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("tr-TR");
  };

  const processedOrders = getFilteredOrders(orders);

  const mmoColDefs = useMemo(
    () => [
      { key: "orderId", value: (o: Order) => o.orderId || "" },
      { key: "firm", value: (o: Order) => o.firm?.firmName || "" },
      { key: "model", value: (o: Order) => o.model?.modelName || "" },
      { key: "quantity", type: "float", value: (o: Order) => o.quantity ?? null },
      { key: "unit", value: (o: Order) => getUnitText(o.unit) },
      { key: "technics", value: (o: Order) => (o.orderTechnics || []).map((ot) => ot.technic?.name || "").filter(Boolean).join(", ") },
      { key: "workshop", value: (o: Order) => o.workshop?.name || "" },
      { key: "status", value: (o: Order) => getStatusText(o.status) },
      { key: "acceptanceDate", type: "datetime", value: (o: Order) => o.acceptanceDate || null },
      { key: "deadline", type: "datetime", value: (o: Order) => o.deadline || null },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const { ctx: mmoCf, filtered: displayOrders } = useTableColumnFilters(processedOrders, mmoColDefs);

  // KPI'lar ve çip sayıları yalnız yüklü satırlardan
  const stats = useMemo(() => {
    const open = orders.filter((o) => isOpenStatus(o.status));
    const left = open.map(daysLeft).filter((d): d is number => d !== null);
    const count = (s: OrderStatus) => orders.filter((o) => o.status === s).length;
    return {
      open: open.length,
      inProgress: count(OrderStatus.Islemde),
      unassigned: count(OrderStatus.Atanmadi),
      late: left.filter((d) => d < 0).length,
      soon: left.filter((d) => d >= 0 && d <= 7).length,
      done: count(OrderStatus.Tamamlandi),
      cancelled: count(OrderStatus.IptalEdildi),
    };
  }, [orders]);
  const chipCount = (k: OrderStatus | "all") =>
    k === "all" ? orders.length
      : k === OrderStatus.Atanmadi ? stats.unassigned
        : k === OrderStatus.Islemde ? stats.inProgress
          : k === OrderStatus.Tamamlandi ? stats.done : stats.cancelled;

  const filterOn = statusFilter !== "all" || !!searchTerm || mmoCf.hasActive;
  const resetFilters = () => {
    setStatusFilter("all");
    setSearchTerm("");
    mmoCf.clearAll();
  };

  const toggleTechnics = (orderId: string, open: boolean) => {
    const newSet = new Set(expandedTechnics);
    if (open) newSet.add(orderId);
    else newSet.delete(orderId);
    setExpandedTechnics(newSet);
  };

  const openImages = (orderId: string) => {
    setSelectedOrderForImages(orderId);
    setShowImageManager(true);
  };

  if (loading && firstLoad) {
    return <PageLoader message="Siparişler yükleniyor..." />;
  }

  return (
    <div className="po-page">
      <header className="po-head">
        <div>
          <span className="po-kicker">Sipariş</span>
          <h1>Üzerinizdeki İşler</h1>
          <p>Size atanan siparişler; durumlarını ve terminlerini buradan izleyin.</p>
        </div>
        <div className="po-head-actions">
          <button type="button" className="po-btn" onClick={loadOrders} disabled={loading}>
            <span className={loading ? "po-spin-icon" : ""} style={{ display: "inline-flex" }}>{I.refresh}</span> Yenile
          </button>
        </div>
      </header>

      <section className="po-kpis mm-kpis">
        <div className="po-kpi navy">
          <span className="l">Açık iş</span>
          <span className="v">{stats.open}<small> sipariş</small></span>
          <span className="s"><b>{stats.inProgress}</b> işlemde · <b>{stats.unassigned}</b> atanmadı</span>
        </div>
        <div className={`po-kpi ${stats.late > 0 ? "err" : "ok"}`}>
          <span className="l">Termini geçen</span>
          <span className="v">{stats.late}</span>
          <span className="s">{stats.late > 0 ? "termin tarihi geçti" : "gecikme yok"}</span>
        </div>
        <div className="po-kpi">
          <span className="l">7 gün içinde termin</span>
          <span className="v">{stats.soon}</span>
          <span className="s">bugünden itibaren 7 gün</span>
        </div>
        <div className="po-kpi ok">
          <span className="l">Tamamlanan</span>
          <span className="v">{stats.done}</span>
          <span className="s">{stats.cancelled > 0 ? <><b>{stats.cancelled}</b> iptal ayrıca</> : "size atanmış toplam"}</span>
        </div>
      </section>

      <section className={`po-card ${loading ? "dim" : ""}`}>
        <div className="po-toolbar">
          <div className="po-search">
            {I.search}
            <input
              placeholder="Firma, model, atölye veya sipariş ID ara"
              aria-label="Sipariş ara"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && <button type="button" className="po-ib" aria-label="Aramayı temizle" onClick={() => setSearchTerm("")}>{I.close}</button>}
          </div>
          <div className="po-chipgroup" role="group" aria-label="Durum">
            {STATUS_CHIPS.map(([k, l]) => (
              <button type="button" key={k} className={statusFilter === k ? "on" : ""} aria-pressed={statusFilter === k} onClick={() => setStatusFilter(k)}>
                {l}<i>{chipCount(k)}</i>
              </button>
            ))}
          </div>
          {filterOn && <button type="button" className="po-clear" onClick={resetFilters}>{I.filterOff} Temizle</button>}
        </div>

        <div className="po-card-head">
          <span><b>{displayOrders.length}</b> sipariş{displayOrders.length !== orders.length && <> · toplam {orders.length}</>}</span>
        </div>

        {displayOrders.length === 0 ? (
          loadError && orders.length === 0 ? (
            <div className="po-blank"><span className="po-blank-icon">{I.inbox}</span><b>Siparişler yüklenemedi</b>Bağlantıyı kontrol edip yeniden deneyin.
              <button type="button" className="po-btn" onClick={loadOrders} disabled={loading}>{I.refresh} Yeniden dene</button></div>
          ) : orders.length === 0 ? (
            <div className="po-blank"><span className="po-blank-icon">{I.inbox}</span><b>Size atanmış sipariş bulunmuyor</b>Bir sipariş size atandığında burada listelenir.</div>
          ) : (
            <div className="po-blank"><span className="po-blank-icon">{I.search}</span><b>Arama kriterine uygun sipariş bulunamadı</b>Süzgeci değiştirin ya da temizleyin.
              <button type="button" className="po-btn" onClick={resetFilters}>{I.filterOff} Süzgeci sıfırla</button></div>
          )
        ) : (
          <div className="po-table-wrap">
            <table className="po-table mm-table">
              <thead>
                <tr>
                  <FilterTh label="Firma" colKey="firm" ctx={mmoCf} />
                  <FilterTh label="Model" colKey="model" ctx={mmoCf} className="po-hide-sm" />
                  <FilterTh label="Miktar" colKey="quantity" type="float" ctx={mmoCf} className="num" />
                  <FilterTh label="Teknikler" colKey="technics" ctx={mmoCf} className="po-hide-md" />
                  <FilterTh label="Atölye" colKey="workshop" ctx={mmoCf} className="po-hide-md" />
                  <FilterTh label="Durum" colKey="status" ctx={mmoCf} />
                  <FilterTh label="Kabul" colKey="acceptanceDate" type="datetime" ctx={mmoCf} className="po-hide-lg" />
                  <FilterTh label="Termin" colKey="deadline" type="datetime" ctx={mmoCf} />
                  <th className="act" aria-label="İşlemler" />
                </tr>
              </thead>
              <tbody>
                {displayOrders.map((order) => {
                  const techs = order.orderTechnics || [];
                  const expanded = expandedTechnics.has(order.orderId);
                  const shownTechs = techs.length <= 3 || expanded ? techs : techs.slice(0, 1);
                  const left = daysLeft(order);
                  const due = left === null ? "" : left < 0 ? "late" : left <= 3 ? "soon" : "";
                  const hasQr = !!(order.qrCodeUrl || order.qrCode);
                  return (
                    <tr
                      key={order.orderId}
                      className={[due === "late" ? "late" : "", order.status === OrderStatus.IptalEdildi ? "off" : ""].join(" ")}
                      onClick={() => navigate(`/orderdetail/${order.orderId}`)}
                    >
                      <td>
                        <div className="po-row">
                          <CopyIdButton value={order.orderId} />
                          <div>
                            <div className="po-name">{order.firm?.firmName || "—"}</div>
                            <div className="po-sub po-only-sm">{order.model?.modelName || "—"}</div>
                            {order.workshop?.name && <div className="po-sub mm-only-md">{order.workshop.name}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="po-hide-sm">
                        <div className="po-name">{order.model?.modelName || "—"}</div>
                        {order.model?.modelCode && order.model.modelCode !== order.model.modelName && <div className="po-sub">{order.model.modelCode}</div>}
                      </td>
                      <td className="num">
                        <span className="po-amt">{(order.quantity || 0).toLocaleString("tr-TR")}</span>
                        <div className="po-sub">{getUnitText(order.unit)}</div>
                      </td>
                      <td className="po-hide-md">
                        {techs.length > 0 ? (
                          <div className="mm-techs" onClick={(e) => e.stopPropagation()}>
                            {shownTechs.map((ot, i) => <span key={i} className="mm-tag">{ot.technic?.name || "-"}</span>)}
                            {techs.length > 3 && (
                              <button type="button" className="mm-more" aria-expanded={expanded} onClick={() => toggleTechnics(order.orderId, !expanded)}>
                                {expanded ? "Gizle" : `+${techs.length - 1} daha`}
                              </button>
                            )}
                          </div>
                        ) : <span className="po-sub">—</span>}
                      </td>
                      <td className="po-hide-md">{order.workshop?.name || <span className="po-sub">—</span>}</td>
                      <td><span className={`po-badge ${STATUS_BADGE[order.status ?? OrderStatus.Atanmadi] || "muted"}`}>{getStatusText(order.status)}</span></td>
                      <td className="po-due po-hide-lg">{formatDate(order.acceptanceDate)}</td>
                      <td className={`po-due ${due}`}>
                        {formatDate(order.deadline)}
                        {left !== null && <div className="po-sub">{left < 0 ? `${-left} gün geçti` : left === 0 ? "bugün" : `${left} gün kaldı`}</div>}
                      </td>
                      <td className="act">
                        <div className="po-rowacts" onClick={(e) => e.stopPropagation()}>
                          {hasQr && <button type="button" className="po-ib" title="QR kodu" aria-label="QR kodu göster" onClick={() => handleShowQrModal(order)}>{I.qr}</button>}
                          <button type="button" className="po-ib" title="Resimleri görüntüle" aria-label="Resimleri görüntüle" onClick={() => openImages(order.orderId)}>{I.image}</button>
                          <button type="button" className="po-ib" title="Detay" aria-label="Sipariş detayı" onClick={() => navigate(`/orderdetail/${order.orderId}`)}>{I.open}</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showImageManager && selectedOrderForImages && (
        <ImageManager
          orderId={selectedOrderForImages}
          onClose={() => {
            setShowImageManager(false);
            setSelectedOrderForImages(null);
          }}
        />
      )}

      {/* QR Modal */}
      {qrPrintModal.show && qrPrintModal.order && (
        <div className="po-overlay center" onMouseDown={(e) => { if (e.target === e.currentTarget) closeQrModal(); }}>
          <div className="po-modal mm-qr" role="dialog" aria-modal="true" aria-labelledby="mm-qr-title">
            <div className="po-modal-head">
              <div>
                <h3 id="mm-qr-title">QR kod</h3>
                <div className="po-sub">{qrPrintModal.order.firm?.firmName || "—"} · {qrPrintModal.order.model?.modelName || "—"}</div>
              </div>
              <button type="button" className="po-ib big" aria-label="Kapat" onClick={closeQrModal}>{I.close}</button>
            </div>
            <div className="po-modal-body">
              {qrPrintModal.qrCodeDataUrl ? (
                <img src={qrPrintModal.qrCodeDataUrl} alt="Sipariş QR kodu" />
              ) : (
                <div className="po-note err">QR kod oluşturulamadı.</div>
              )}
            </div>
            <div className="po-modal-foot">
              <button type="button" className="po-btn" onClick={closeQrModal}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
    </div>
  );
};

export default ModelistMyOrders;
